/**
 * AI Conversation List Component
 *
 * Displays list of AI-handled conversations with:
 * - Caller information
 * - Call status and duration
 * - Follow-up indicators
 * - Search and filter capabilities
 * - Real-time updates
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Phone,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  Clock,
  AlertCircle,
  Search,
  CheckCircle2,
  User
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { AIConversationSummary, CallStatus, Sentiment } from '@/types/ai-support';
import { cn } from '@/lib/utils';

interface AIConversationListProps {
  conversations: AIConversationSummary[];
  selectedId: string | null;
  onSelectConversation: (id: string) => void;
}

export const AIConversationList: React.FC<AIConversationListProps> = ({
  conversations,
  selectedId,
  onSelectConversation
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | CallStatus>('all');
  const [followupFilter, setFollowupFilter] = useState<'all' | 'pending' | 'completed'>('all');

  // Filter and search conversations
  const filteredConversations = useMemo(() => {
    return conversations.filter(conv => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        conv.callerName?.toLowerCase().includes(searchLower) ||
        conv.callerPhone.includes(searchQuery) ||
        conv.summary?.toLowerCase().includes(searchLower) ||
        conv.intent?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = statusFilter === 'all' || conv.callStatus === statusFilter;

      // Follow-up filter
      const matchesFollowup =
        followupFilter === 'all' ||
        (followupFilter === 'pending' && conv.requiresFollowup && !conv.followupCompletedAt) ||
        (followupFilter === 'completed' && conv.followupCompletedAt);

      return matchesSearch && matchesStatus && matchesFollowup;
    });
  }, [conversations, searchQuery, statusFilter, followupFilter]);

  // Get status icon
  const getStatusIcon = (status: CallStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'failed':
      case 'no-answer':
        return <PhoneMissed className="h-4 w-4 text-red-600" />;
      case 'busy':
        return <PhoneOff className="h-4 w-4 text-orange-600" />;
      case 'in-progress':
        return <PhoneCall className="h-4 w-4 text-blue-600 animate-pulse" />;
      default:
        return <Phone className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Get status badge variant
  const getStatusVariant = (status: CallStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'failed':
      case 'no-answer':
        return 'destructive';
      case 'in-progress':
        return 'default';
      default:
        return 'secondary';
    }
  };

  // Get sentiment color
  const getSentimentColor = (sentiment?: Sentiment) => {
    switch (sentiment) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  };

  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) {
return 'N/A';
}
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Conversations
          <Badge variant="secondary" className="ml-auto">
            {filteredConversations.length}
          </Badge>
        </CardTitle>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | CallStatus)}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="no-answer">No Answer</SelectItem>
            </SelectContent>
          </Select>

          <Select value={followupFilter} onValueChange={(value) => setFollowupFilter(value as 'all' | 'pending' | 'completed')}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Follow-up" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Needs Follow-up</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full">
          <div className="space-y-2 p-4 pt-0">
            {filteredConversations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Phone className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No conversations found</p>
                {searchQuery && (
                  <Button
                    variant="link"
                    onClick={() => setSearchQuery('')}
                    className="mt-2"
                  >
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  type="button"
                  key={conv.id}
                  onClick={() => onSelectConversation(conv.id)}
                  className={cn(
                    'w-full cursor-pointer rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selectedId === conv.id
                      ? 'bg-primary/5 border-primary'
                      : 'hover:bg-muted/50'
                  )}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getStatusIcon(conv.callStatus)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {conv.callerName || conv.contactName || conv.callerPhone}
                        </p>
                        {conv.callerName && (
                          <p className="text-xs text-muted-foreground">{conv.callerPhone}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant={getStatusVariant(conv.callStatus)} className="text-xs">
                      {conv.callStatus}
                    </Badge>
                  </div>

                  {/* Summary/Intent */}
                  {(conv.summary || conv.intent) && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {conv.summary || conv.intent}
                    </p>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {conv.callStartedAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(conv.callStartedAt), { addSuffix: true })}
                      </span>
                    )}
                    {conv.callDurationSeconds && (
                      <span className="flex items-center gap-1">
                        <PhoneCall className="h-3 w-3" />
                        {formatDuration(conv.callDurationSeconds)}
                      </span>
                    )}
                    {conv.sentiment && (
                      <span className={cn('flex items-center gap-1', getSentimentColor(conv.sentiment))}>
                        {conv.sentiment === 'positive' ? '😊' : conv.sentiment === 'negative' ? '😟' : '😐'}
                        {conv.sentiment}
                      </span>
                    )}
                  </div>

                  {/* Follow-up Indicator */}
                  {conv.requiresFollowup && !conv.followupCompletedAt && (
                    <div className="mt-2 pt-2 border-t">
                      <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Requires Follow-up
                        {conv.followupPriority && ` - ${conv.followupPriority}`}
                      </Badge>
                    </div>
                  )}

                  {/* Assigned to */}
                  {conv.assignedTo && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span>
                        Assigned to {conv.assignedToFirstName} {conv.assignedToLastName}
                      </span>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
